import React from "react";

import {
    FiCheckCircle,
    FiClock,
    FiDollarSign,
    FiPackage,
    FiXCircle,
    FiFileText
} from "react-icons/fi";

import "../DebitNotes.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );

/*
|--------------------------------------------------------------------------
| GET TOTALS
|--------------------------------------------------------------------------
*/

const getTotals = (
    debitNote
) => {
    const totals =
        debitNote?.totals ||
        {};

    const grossAmount =
        roundMoney(
            totals?.grossAmount ??
            debitNote?.grossAmount ??
            debitNote?.subTotal ??
            0
        );

    const discountAmount =
        roundMoney(
            totals?.discountAmount ??
            debitNote?.discountAmount ??
            0
        );

    const taxableAmount =
        roundMoney(
            totals?.taxableAmount ??
            debitNote?.taxableAmount ??
            Math.max(
                grossAmount -
                    discountAmount,
                0
            )
        );

    const cgstAmount =
        roundMoney(
            totals?.cgstAmount ??
            debitNote?.cgstAmount ??
            0
        );

    const sgstAmount =
        roundMoney(
            totals?.sgstAmount ??
            debitNote?.sgstAmount ??
            0
        );

    const igstAmount =
        roundMoney(
            totals?.igstAmount ??
            debitNote?.igstAmount ??
            0
        );

    const cessAmount =
        roundMoney(
            totals?.cessAmount ??
            debitNote?.cessAmount ??
            0
        );

    /*
    |--------------------------------------------------------------------------
    | TOTAL TAX
    |--------------------------------------------------------------------------
    */

    const calculatedTax =
        roundMoney(
            cgstAmount +
            sgstAmount +
            igstAmount +
            cessAmount
        );

    const totalTax =
        roundMoney(
            totals?.totalTax ??
            totals?.taxAmount ??
            debitNote?.totalTax ??
            debitNote?.taxAmount ??
            calculatedTax
        );

    const roundOff =
        roundMoney(
            totals?.roundOff ??
            totals?.roundOffAmount ??
            debitNote?.roundOff ??
            debitNote?.roundOffAmount ??
            0
        );

    const debitAmount =
        roundMoney(
            totals?.debitAmount ??
            totals?.grandTotal ??
            totals?.totalAmount ??
            debitNote?.debitAmount ??
            debitNote?.grandTotal ??
            debitNote?.totalAmount ??
            taxableAmount +
                totalTax +
                roundOff
        );

    return {
        grossAmount,
        discountAmount,
        taxableAmount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        cessAmount,
        totalTax,
        roundOff,
        debitAmount,
    };
};

/*
|--------------------------------------------------------------------------
| STATUS HELPERS
|--------------------------------------------------------------------------
*/

const getStockStatus = (
    debitNote
) => {
    const items =
        Array.isArray(
            debitNote?.items
        )
            ? debitNote.items
            : [];

    const stockItems =
        items.filter(
            (item) =>
                item?.reduceStock !==
                false
        );

    if (
        stockItems.length ===
        0
    ) {
        return {
            status:
                "NOT_REQUIRED",

            label:
                "Not Required",
        };
    }

    const allReduced =
        stockItems.every(
            (item) =>
                item?.stockReduced ===
                true
        );

    if (allReduced) {
        return {
            status:
                "COMPLETED",

            label:
                "Stock Reduced",
        };
    }

    const someReduced =
        stockItems.some(
            (item) =>
                item?.stockReduced ===
                true
        );

    if (someReduced) {
        return {
            status:
                "PARTIAL",

            label:
                "Partially Reduced",
        };
    }

    return {
        status:
            "PENDING",

        label:
            "Pending",
    };
};

const getLedgerStatus = (
    debitNote
) => {
    /*
    |--------------------------------------------------------------------------
    | SUPPORT POSSIBLE BACKEND FIELD NAMES
    |--------------------------------------------------------------------------
    */

    const posted =
        debitNote?.ledgerPosted ===
            true ||
        debitNote?.isLedgerPosted ===
            true ||
        debitNote?.ledgerEntryCreated ===
            true ||
        Boolean(
            debitNote?.ledgerEntry
        );

    if (posted) {
        return {
            status:
                "COMPLETED",

            label:
                "Posted",
        };
    }

    if (
        String(
            debitNote?.status ||
                ""
        ).toUpperCase() ===
        "CONFIRMED"
    ) {
        return {
            status:
                "PENDING",

            label:
                "Pending",
        };
    }

    return {
        status:
            "NOT_POSTED",

        label:
            "Not Posted",
    };
};

/*
|--------------------------------------------------------------------------
| STATUS COMPONENT
|--------------------------------------------------------------------------
*/

const ProcessStatus = ({
    title,
    status,
    label,
    icon,
}) => {
    let statusClass =
        "debit-note-status-draft";

    let StatusIcon =
        FiClock;

    if (
        status ===
        "COMPLETED"
    ) {
        statusClass =
            "debit-note-status-confirmed";

        StatusIcon =
            FiCheckCircle;
    }

    if (
        status ===
            "NOT_REQUIRED" ||
        status ===
            "NOT_POSTED"
    ) {
        statusClass =
            "debit-note-status-cancelled";

        StatusIcon =
            FiXCircle;
    }

    return (
        <div className="debit-note-summary-card">
            <span>
                {title}
            </span>

            <strong
                style={{
                    display:
                        "flex",

                    alignItems:
                        "center",

                    gap:
                        "7px",
                }}
            >
                {icon}

                <span
                    className={`debit-note-status ${statusClass}`}
                >
                    <StatusIcon />

                    {label}
                </span>
            </strong>
        </div>
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const DebitNoteSummary = ({
    debitNote,
}) => {
    if (!debitNote) {
        return null;
    }

    const totals =
        getTotals(
            debitNote
        );

    const stockStatus =
        getStockStatus(
            debitNote
        );

    const ledgerStatus =
        getLedgerStatus(
            debitNote
        );

    const showGSTBreakup =
        totals.cgstAmount >
            0 ||
        totals.sgstAmount >
            0 ||
        totals.igstAmount >
            0 ||
        totals.cessAmount >
            0;

    return (
        <>
            {/* =========================================================
                AMOUNT SUMMARY
               ========================================================= */}

            <section className="debit-note-view-card">
                <div
                    style={{
                        display:
                            "flex",

                        alignItems:
                            "center",

                        justifyContent:
                            "space-between",

                        gap:
                            "10px",

                        marginBottom:
                            "14px",
                    }}
                >
                    <div>
                        <h3
                            style={{
                                margin:
                                    0,

                                color:
                                    "#172033",

                                fontSize:
                                    "14px",

                                fontWeight:
                                    850,
                            }}
                        >
                            Debit Note Summary
                        </h3>

                        <p
                            style={{
                                margin:
                                    "3px 0 0",

                                color:
                                    "#64748b",

                                fontSize:
                                    "10px",
                            }}
                        >
                            Purchase return amount and tax calculation.
                        </p>
                    </div>

                    <FiDollarSign
                        style={{
                            color:
                                "#2563eb",

                            fontSize:
                                "20px",
                        }}
                    />
                </div>

                {/* BASIC AMOUNTS */}

                <div className="debit-note-summary-grid">
                    <div className="debit-note-summary-card">
                        <span>
                            Gross Amount
                        </span>

                        <strong>
                            {formatCurrency(
                                totals.grossAmount
                            )}
                        </strong>

                        <small>
                            Before discount
                        </small>
                    </div>

                    <div className="debit-note-summary-card">
                        <span>
                            Discount
                        </span>

                        <strong>
                            {formatCurrency(
                                totals.discountAmount
                            )}
                        </strong>

                        <small>
                            Returned item discount
                        </small>
                    </div>

                    <div className="debit-note-summary-card">
                        <span>
                            Taxable Amount
                        </span>

                        <strong>
                            {formatCurrency(
                                totals.taxableAmount
                            )}
                        </strong>

                        <small>
                            Amount before GST
                        </small>
                    </div>

                    <div className="debit-note-summary-card">
                        <span>
                            Total Tax
                        </span>

                        <strong>
                            {formatCurrency(
                                totals.totalTax
                            )}
                        </strong>

                        <small>
                            GST / tax amount
                        </small>
                    </div>
                </div>

                {/* GST BREAKUP */}

                {showGSTBreakup && (
                    <div className="debit-note-summary-grid">
                        <div className="debit-note-summary-card">
                            <span>
                                CGST
                            </span>

                            <strong>
                                {formatCurrency(
                                    totals.cgstAmount
                                )}
                            </strong>
                        </div>

                        <div className="debit-note-summary-card">
                            <span>
                                SGST
                            </span>

                            <strong>
                                {formatCurrency(
                                    totals.sgstAmount
                                )}
                            </strong>
                        </div>

                        <div className="debit-note-summary-card">
                            <span>
                                IGST
                            </span>

                            <strong>
                                {formatCurrency(
                                    totals.igstAmount
                                )}
                            </strong>
                        </div>

                        <div className="debit-note-summary-card">
                            <span>
                                Cess
                            </span>

                            <strong>
                                {formatCurrency(
                                    totals.cessAmount
                                )}
                            </strong>
                        </div>
                    </div>
                )}

                {/* FINAL AMOUNT */}

                <div
                    style={{
                        display:
                            "flex",

                        justifyContent:
                            "flex-end",

                        marginTop:
                            "12px",
                    }}
                >
                    <div
                        style={{
                            width:
                                "min(420px, 100%)",

                            border:
                                "1px solid #bfdbfe",

                            borderRadius:
                                "10px",

                            overflow:
                                "hidden",

                            background:
                                "#ffffff",
                        }}
                    >
                        <div
                            style={{
                                display:
                                    "flex",

                                justifyContent:
                                    "space-between",

                                padding:
                                    "11px 14px",

                                borderBottom:
                                    "1px solid #e2e8f0",

                                fontSize:
                                    "11px",
                            }}
                        >
                            <span>
                                Taxable Amount
                            </span>

                            <strong>
                                {formatCurrency(
                                    totals.taxableAmount
                                )}
                            </strong>
                        </div>

                        <div
                            style={{
                                display:
                                    "flex",

                                justifyContent:
                                    "space-between",

                                padding:
                                    "11px 14px",

                                borderBottom:
                                    "1px solid #e2e8f0",

                                fontSize:
                                    "11px",
                            }}
                        >
                            <span>
                                Total Tax
                            </span>

                            <strong>
                                {formatCurrency(
                                    totals.totalTax
                                )}
                            </strong>
                        </div>

                        {totals.roundOff !==
                            0 && (
                            <div
                                style={{
                                    display:
                                        "flex",

                                    justifyContent:
                                        "space-between",

                                    padding:
                                        "11px 14px",

                                    borderBottom:
                                        "1px solid #e2e8f0",

                                    fontSize:
                                        "11px",
                                }}
                            >
                                <span>
                                    Round Off
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals.roundOff
                                    )}
                                </strong>
                            </div>
                        )}

                        <div
                            style={{
                                display:
                                    "flex",

                                alignItems:
                                    "center",

                                justifyContent:
                                    "space-between",

                                padding:
                                    "14px",

                                background:
                                    "#eff6ff",
                            }}
                        >
                            <div>
                                <span
                                    style={{
                                        display:
                                            "block",

                                        color:
                                            "#1e40af",

                                        fontSize:
                                            "9px",

                                        fontWeight:
                                            850,

                                        textTransform:
                                            "uppercase",
                                    }}
                                >
                                    Debit Amount
                                </span>

                                <small
                                    style={{
                                        color:
                                            "#64748b",

                                        fontSize:
                                            "9px",
                                    }}
                                >
                                    Supplier payable adjustment
                                </small>
                            </div>

                            <strong
                                style={{
                                    color:
                                        "#1d4ed8",

                                    fontSize:
                                        "20px",

                                    fontWeight:
                                        900,
                                }}
                            >
                                {formatCurrency(
                                    totals.debitAmount
                                )}
                            </strong>
                        </div>
                    </div>
                </div>
            </section>

            {/* =========================================================
                POSTING / INVENTORY STATUS
               ========================================================= */}

            <section className="debit-note-view-card">
                <div
                    style={{
                        marginBottom:
                            "12px",
                    }}
                >
                    <h3
                        style={{
                            margin:
                                0,

                            color:
                                "#172033",

                            fontSize:
                                "14px",

                            fontWeight:
                                850,
                        }}
                    >
                        Processing Status
                    </h3>

                    <p
                        style={{
                            margin:
                                "3px 0 0",

                            color:
                                "#64748b",

                            fontSize:
                                "10px",
                        }}
                    >
                        Inventory and accounting posting status.
                    </p>
                </div>

                <div
                    className="debit-note-summary-grid"
                    style={{
                        marginBottom:
                            0,
                    }}
                >
                    <ProcessStatus
                        title="Inventory"
                        status={
                            stockStatus.status
                        }
                        label={
                            stockStatus.label
                        }
                        icon={
                            <FiPackage />
                        }
                    />

                    <ProcessStatus
                        title="Supplier Ledger"
                        status={
                            ledgerStatus.status
                        }
                        label={
                            ledgerStatus.label
                        }
                        icon={
                            <FiFileText />
                        }
                    />

                    <div className="debit-note-summary-card">
                        <span>
                            Document Status
                        </span>

                        <strong>
                            {String(
                                debitNote?.status ||
                                    "DRAFT"
                            )
                                .replace(
                                    /_/g,
                                    " "
                                )
                                .toUpperCase()}
                        </strong>

                        <small>
                            Debit note lifecycle
                        </small>
                    </div>

                    <div className="debit-note-summary-card debit-note-summary-card-amount">
                        <span>
                            Final Debit
                        </span>

                        <strong>
                            {formatCurrency(
                                totals.debitAmount
                            )}
                        </strong>

                        <small>
                            Purchase adjustment
                        </small>
                    </div>
                </div>
            </section>
        </>
    );
};

export default DebitNoteSummary;